import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DictionaryEntry } from '../types';
import { Header } from './Header';
import { Footer } from './Footer';
import { DefinitionFooter } from './DefinitionFooter';

const getTheme = (index: number = 0) => {
    const themes = ['cyan', 'fuchsia', 'violet'] as const;
    return themes[index % themes.length];
};

const getThemeColors = (theme: 'cyan' | 'fuchsia' | 'violet'): {
    text: string;
    border: string;
    bg: string;
    cardBg: string;
    cardBorder: string;
    tagBg: string;
    tagBorder: string;
} => {
    switch (theme) {
        case 'cyan':
            return {
                text: 'text-cyan-400',
                border: 'border-cyan-500/30',
                bg: 'bg-cyan-500/10',
                cardBg: 'from-slate-900 via-slate-900 to-cyan-950/40',
                cardBorder: 'border-cyan-500/20',
                tagBg: 'bg-cyan-500/20',
                tagBorder: 'border-cyan-500/30'
            };
        case 'fuchsia':
            return {
                text: 'text-fuchsia-400',
                border: 'border-fuchsia-500/30',
                bg: 'bg-fuchsia-500/10',
                cardBg: 'from-slate-900 via-slate-900 to-fuchsia-950/40',
                cardBorder: 'border-fuchsia-500/20',
                tagBg: 'bg-fuchsia-500/20',
                tagBorder: 'border-fuchsia-500/30'
            };
        case 'violet':
            return {
                text: 'text-violet-400',
                border: 'border-violet-500/30',
                bg: 'bg-violet-500/10',
                cardBg: 'from-slate-900 via-slate-900 to-violet-950/40',
                cardBorder: 'border-violet-500/20',
                tagBg: 'bg-violet-500/20',
                tagBorder: 'border-violet-500/30'
            };
    }
};

export const DefinitionPageCyberpunk = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [entry, setEntry] = useState<DictionaryEntry | null>(null);
    const [loading, setLoading] = useState(true);
    const [themeIndex, setThemeIndex] = useState(0);

    useEffect(() => {
        const fetchEntry = async () => {
            if (!slug) return;
            setLoading(true);

            // On cherche par le terme normalisé (minuscule)
            const term = decodeURIComponent(slug).toLowerCase();

            const { data, error } = await supabase
                .from('dictionary_entries')
                .select('*')
                .ilike('term', term)
                .single();

            if (error) {
                console.error('Error fetching entry:', error);
            } else if (data) {
                setEntry({
                    ...data,
                    fullDefinition: data.full_definition,
                    image: data.image_url,
                    isFeatured: data.is_featured
                });

                // Calculer l'index du thème basé sur l'ID
                setThemeIndex(data.id || 0);
            }
            setLoading(false);
        };

        fetchEntry();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4">
                <Loader2 className="animate-spin text-cyan-400" size={32} />
                <p className="text-slate-400 font-medium font-mono">Chargement du terme...</p>
            </div>
        );
    }

    if (!entry) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
                <h1 className="text-3xl font-sans font-bold text-white mb-4">Terme introuvable</h1>
                <p className="text-slate-400 mb-8 max-w-md">Désolé, nous n'avons pas trouvé de définition pour "{decodeURIComponent(slug || '')}".</p>
                <button
                    onClick={() => navigate('/')}
                    className="bg-cyan-500 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:bg-cyan-600 transition-all hover:-translate-y-1 active:scale-95"
                >
                    Retour au dictionnaire
                </button>
            </div>
        )
    }

    const theme = getTheme(themeIndex);
    const themeColors = getThemeColors(theme);

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col">
            <Header />

            <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
                <button
                    onClick={() => navigate('/')}
                    className="group flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors mb-8 font-mono"
                >
                    <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    <span>Retour au dictionnaire</span>
                </button>

                <article className={`relative bg-gradient-to-br ${themeColors.cardBg} border ${themeColors.cardBorder} rounded-2xl p-10 md:p-12 shadow-2xl overflow-hidden`}>
                    {/* Halo lumineux radial en haut à droite */}
                    <div
                        className="absolute top-0 right-0 w-64 h-64 opacity-30 pointer-events-none"
                        style={{
                            background: `radial-gradient(circle at top right, ${theme === 'cyan' ? 'rgba(34, 211, 238, 0.4)' :
                                theme === 'fuchsia' ? 'rgba(217, 70, 239, 0.4)' :
                                    'rgba(139, 92, 246, 0.4)'
                                }, transparent 70%)`
                        }}
                    ></div>

                    <div className="relative z-10">
                        {/* Header */}
                        <div className="mb-6">
                            <h1 className="text-5xl md:text-7xl font-sans font-bold text-white tracking-tight leading-none">
                                {entry.term}
                            </h1>
                        </div>

                        {/* Définition courte (Lead) */}
                        <div className="mb-10">
                            <p className="text-2xl md:text-3xl leading-tight">
                                <span className={`font-mono font-bold mr-3 ${themeColors.text}`}>{entry.type.toLowerCase()}</span>
                                <span className="text-slate-300 font-light">
                                    {entry.definition.split(' ').slice(0, -1).join(' ')} <span className="whitespace-nowrap">{entry.definition.split(' ').slice(-1)}</span>
                                </span>
                            </p>
                        </div>

                        {/* Image d'illustration */}
                        {entry.image && (
                            <div className="mb-12 rounded-2xl overflow-hidden shadow-lg max-w-2xl mx-auto border border-slate-800/50">
                                <div className="aspect-square w-full">
                                    <img
                                        src={entry.image}
                                        alt={`Illustration pour ${entry.term}`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Définition complète */}
                        <div className="prose prose-invert prose-lg md:prose-xl max-w-none text-slate-300 mb-10">
                            {entry.fullDefinition.split(/\n|\\n/).map((paragraph, index) => (
                                <p key={index} className="leading-relaxed mb-6 last:mb-0 font-light">
                                    {paragraph.trim()}
                                </p>
                            ))}
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-3 pt-8 mt-12 border-t border-slate-800/50">
                            {entry.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className={`px-4 py-2 ${themeColors.tagBg} ${themeColors.text} text-sm font-mono font-bold rounded-lg border ${themeColors.tagBorder}`}
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                        {/* Footer */}
                        <DefinitionFooter
                            entry={entry}
                            variant="cyberpunk"
                            accentColor={themeColors.text}
                        />
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
};
